import { createStyles, Group, Modal, rem, SimpleGrid, Stack, Tooltip } from '@mantine/core';
import H3Medium from 'components/typography/H3Medium';
import RevisionField from 'components/forms/revision/fill-out/RevisionField';
import DeviceFaultsButton from 'components/forms/revision/fill-out/devices/DeviceFaultsButton';
import DeviceMeasurementButton from 'components/forms/revision/fill-out/devices/DeviceMeasurementButton';
import { RevisionDevice } from 'components/forms/revision/fill-out/types';
import { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import P3Regular from 'components/typography/P3Regular';
import {
  FEATURE_TOGGLE_SHOW_DEVICE_ID_IN_REVISION_FILL_OUT_FORM,
  FEATURE_TOGGLE_SHOW_DEVICE_LINK_IN_REVISION_FILL_OUT_FORM,
} from 'env';
import { IconArrowUpLeft, IconCloudDown, IconExternalLink } from '@tabler/icons-react';
import ActionIcon from 'components/ActionIcon';
import DeviceStructureIcon from 'components/DeviceStructureIcon';
import { useApi } from 'api/api-context';
import { EDIT_DEVICE_PAGE_PATH } from 'routes/paths';
import panic from 'errors/panic';
import { useFillOutRevisionSaveDraftProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionSaveDraftProvider';
import Button from 'components/Button';
import P1Regular from 'components/typography/P1Regular';
import { useLocation, useNavigate } from 'react-router-dom';
import { createDeviceFormReferrerState } from 'components/forms/device/device-form-referrer';

interface DeviceSectionProps {
  device: RevisionDevice;
  hasParentDevice?: boolean;
  onRemoteSourceSync?: () => void;
}

const useStyles = createStyles((theme) => ({
  header: {
    border: `${rem(1)} solid ${theme.colors.gray[2]}`,
    borderTopLeftRadius: rem(4),
    borderTopRightRadius: rem(4),
  },
  body: {
    borderLeft: `${rem(1)} solid ${theme.colors.gray[2]}`,
    borderRight: `${rem(1)} solid ${theme.colors.gray[2]}`,
    borderBottom: `${rem(1)} solid ${theme.colors.gray[2]}`,
    borderBottomLeftRadius: rem(4),
    borderBottomRightRadius: rem(4),
  },
}));

/**
 * A single device in the revision form.
 */
export default function DeviceSection({ device, hasParentDevice, onRemoteSourceSync }: DeviceSectionProps) {
  const { classes } = useStyles();
  const { sectionsByTab, form, revision } = useFillOutRevisionDataProvider();
  const { saveDraft, loading: draftLoading } = useFillOutRevisionSaveDraftProvider();
  const { getAction } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [deviceDetailModalOpened, setDeviceDetailModalOpened] = useState(false);
  const [savingBeforeNavigate, setSavingBeforeNavigate] = useState(false);

  const rows = useMemo(() => sectionsByTab.device[0]?.rows ?? [], [sectionsByTab]);
  const namePrefix = useMemo(() => `devices.${device.deviceId}.fields.`, [device.deviceId]);
  const deviceDetailLink = useMemo(
    () => EDIT_DEVICE_PAGE_PATH.insert({ deviceId: device.deviceId }),
    [device.deviceId]
  );
  const returnTo = useMemo(() => `${location.pathname}${location.search}`, [location.pathname, location.search]);
  const isSavingBeforeNavigate = savingBeforeNavigate || draftLoading;

  const [canSeeDevice, setCanSeeDevice] = useState(false);

  useEffect(() => {
    if (!FEATURE_TOGGLE_SHOW_DEVICE_LINK_IN_REVISION_FILL_OUT_FORM) {
      return;
    }

    const deviceListAction = getAction('DeviceList');

    deviceListAction({ query: { filters: { 'deviceId.eq': device.deviceId } } })
      .then((response) => setCanSeeDevice(response.length > 0))
      .catch(panic);
  }, [device.deviceId]);

  const nFaults = useMemo(() => {
    let nFaults = device.faults.length;

    for (const measurementList of Object.values(device.measurements)) {
      for (const measurement of measurementList) {
        nFaults += measurement._faults.length;
      }
    }

    return nFaults;
  }, [device]);

  const nFaultAttachments = useMemo(() => {
    let count = 0;

    for (const fault of device.faults) {
      count += fault.attachments?.length ?? 0;
    }

    for (const measurementList of Object.values(device.measurements)) {
      for (const measurement of measurementList) {
        for (const fault of measurement._faults) {
          count += fault.attachments?.length ?? 0;
        }
      }
    }

    return count;
  }, [device]);

  useEffect(() => {
    form.setFieldValue(`devices.${device.deviceId}.fields._deviceFaultCount`, nFaults);
  }, [device.deviceId, nFaults]);

  useEffect(() => {
    form.setFieldValue(`devices.${device.deviceId}.fields._deviceFaultAttachmentCount`, nFaultAttachments);
  }, [device.deviceId, nFaultAttachments]);

  /**
   * Opens the confirmation modal before navigating to device detail.
   */
  const openDeviceDetailModal: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDeviceDetailModalOpened(true);
  };

  /**
   * Saves the draft and navigates to device detail with referrer state.
   */
  async function navigateToDeviceDetail() {
    try {
      setSavingBeforeNavigate(true);
      await saveDraft({ notify: false, throwOnError: true });
      navigate(deviceDetailLink, {
        state: createDeviceFormReferrerState({
          kind: revision.kind === 'revision' ? 'revision' : 'form',
          revisionId: String(revision.revisionId),
          returnTo,
        }),
      });
    } catch {
      // saveDraft already reports the error through panic when throwOnError is enabled.
    } finally {
      setSavingBeforeNavigate(false);
    }
  }

  return (
    <>
      <Modal
        opened={deviceDetailModalOpened}
        onClose={() => setDeviceDetailModalOpened(false)}
        title="Otvoriť detail zariadenia"
        centered
        closeOnClickOutside={!isSavingBeforeNavigate}
        closeOnEscape={!isSavingBeforeNavigate}
        closeButtonProps={{ loading: isSavingBeforeNavigate }}
      >
        <Stack spacing={24}>
          <P1Regular>Pred otvorením detailu zariadenia sa uloží aktuálny koncept.</P1Regular>
          <Group position="right" spacing={16}>
            <Button
              variant="subtle-gray"
              onClick={() => setDeviceDetailModalOpened(false)}
              disabled={isSavingBeforeNavigate}
            >
              Zrušiť
            </Button>
            <Button variant="primary" onClick={navigateToDeviceDetail} loading={isSavingBeforeNavigate} data-autofocus>
              Pokračovať
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Stack spacing={0}>
        <Group bg="gray.0" className={classes.header} position="apart" p={24}>
          <Group spacing={8}>
            {hasParentDevice && device.fields._isChildDevice && (
              <Tooltip label="Podradené zariadenie" openDelay={200} withinPortal>
                <IconArrowUpLeft size={16} stroke={1.8} />
              </Tooltip>
            )}
            <H3Medium>{device.fields._deviceName}</H3Medium>
            {FEATURE_TOGGLE_SHOW_DEVICE_LINK_IN_REVISION_FILL_OUT_FORM && canSeeDevice && (
              <Tooltip label="Detail zariadenia" openDelay={200} withinPortal withArrow={false}>
                <ActionIcon
                  size="md"
                  variant="subtle"
                  component="a"
                  href={deviceDetailLink}
                  target="_self"
                  onClick={openDeviceDetailModal}
                >
                  <IconExternalLink size={16} stroke={1.8} />
                </ActionIcon>
              </Tooltip>
            )}
            {FEATURE_TOGGLE_SHOW_DEVICE_ID_IN_REVISION_FILL_OUT_FORM && (
              <P3Regular c="gray.6">(ID: {device.deviceId})</P3Regular>
            )}
            {!!device.fields._deviceStructure && <DeviceStructureIcon structure={device.fields._deviceStructure} />}
            {device.fields._deviceRemoteSourceId && (
              <Tooltip label="Synchronizovať zariadenia so vzdialeným zdrojom" withArrow={false} openDelay={200}>
                <ActionIcon size="md" variant="subtle" onClick={onRemoteSourceSync}>
                  <IconCloudDown />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <Group spacing={16}>
            {sectionsByTab.measurements
              .filter(() => !device.fields._isParentDevice) // No measurements for parent devices
              .map((measurement) => (
                <DeviceMeasurementButton key={measurement.id} device={device} measurement={measurement} />
              ))}
            <DeviceFaultsButton device={device} />
          </Group>
        </Group>
        {!device.fields._isParentDevice && (
          <Stack p={24} spacing={16} className={classes.body}>
            {rows.map((row) => (
              <SimpleGrid key={row.id} cols={row.inputs.length} spacing={24}>
                {row.inputs.map((input) => (
                  <RevisionField key={input.id} field={input} namePrefix={namePrefix} deviceId={device.deviceId} />
                ))}
              </SimpleGrid>
            ))}
          </Stack>
        )}
      </Stack>
    </>
  );
}
